import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useJourney } from '../JourneyContext';
import api from '../../../api';
import { toast } from '../../../utils/toast';
import { Card, Button } from '../../ui';

// Summary section component
const SummarySection = ({ title, icon, children, onEdit }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="text-blue-500">{icon}</div>
        <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </button>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// Info row component
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="font-medium text-gray-900 dark:text-white">{value || '—'}</span>
  </div>
);

export default function ReviewStep({ onComplete, onPrev }) {
  const { company, branches, onboarding, goToStep, getTotals, deleteDraft, journeyData } = useJourney();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const totals = getTotals();

  // Fetch assignable users to display names/roles
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/users/assignable');
        setAssignableUsers(response.data);
      } catch (error) {
        console.error('Error fetching assignable users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Get selected team members
  const selectedTeamMembers = assignableUsers.filter(user => 
    (company.technicianIds || []).includes(user.id)
  );

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // 1. Create the company
      const allowedServerLocations = ['Head Office', 'Cloud Based', 'Per Branch'];
      const normalizedServerLocation = onboarding.hasServers
        ? (allowedServerLocations.includes(onboarding.serverLocation) ? onboarding.serverLocation : 'Head Office')
        : null;

      const companyPayload = {
        name: company.name,
        registrationNumber: company.registrationNumber,
        vatNumber: company.vatNumber,
        address: company.address,
        city: company.city,
        province: company.province,
        postalCode: company.postalCode,
        country: company.country,
        contactPerson: company.contactPerson,
        contactNumber: company.contactNumber,
        emailAddress: company.emailAddress,
        secondaryContact: company.secondaryContact,
        billingContactName: company.billingContactName,
        billingEmail: company.billingEmail,
        billingAddress: company.billingAddress,
        paymentTerms: company.paymentTerms,
        numberOfBranches: branches.length,
        hasServers: onboarding.hasServers,
        serverLocation: normalizedServerLocation,
        technicianIds: company.technicianIds || [],
      };

      const companyResponse = await api.post('/companies', companyPayload);
      const createdCompany = companyResponse.data;
      
      // 2. Create branches
      const createdBranches = [];
      for (const branch of branches) {
        const branchPayload = {
          name: branch.name,
          contactPerson: branch.contactPerson,
          contactNumber: branch.contactNumber,
          emailAddress: branch.emailAddress,
          address: branch.address,
          city: branch.city,
          province: branch.province,
          postalCode: branch.postalCode,
          country: branch.country,
          hasFirewall: branch.hasFirewall,
          numberOfUsers: parseInt(branch.numberOfUsers) || 0,
          numberOfDevices: parseInt(branch.numberOfDevices) || 0,
          numberOfNetworkDevices: parseInt(branch.numberOfNetworkDevices) || 0,
          numberOfServers: parseInt(branch.numberOfServers) || 0,
        };
        
        const branchResponse = await api.post(`/companies/${createdCompany.id}/branches`, branchPayload);
        createdBranches.push(branchResponse.data);
      }

      // 3. Create onboarding tasks
      const datePayload = {
        startDate: onboarding.onboardingStartDate || null,
        completionDate: onboarding.onboardingEndDate || null,
      };

      // Create internal system tasks
      for (const system of onboarding.systems) {
        await api.post(`/tasks/company/${createdCompany.id}`, {
          type: system,
          status: 'Not Started',
          ...datePayload,
        });
      }

      // Create company-wide tasks
      for (const task of onboarding.companyTasks) {
        await api.post(`/tasks/company/${createdCompany.id}`, {
          type: task,
          status: 'Not Started',
          ...datePayload,
        });
      }

      // Create branch-specific service tasks (for UI display only, not counted in progress)
      for (let i = 0; i < createdBranches.length; i++) {
        const createdBranch = createdBranches[i];
        const branchServices = onboarding.branchServices[i] || [];
        
        // Create CompanyTask for each selected branch service
        for (const service of branchServices) {
          await api.post(`/tasks/company/${createdCompany.id}`, {
            type: service,
            status: 'Not Started',
            branchId: createdBranch.id,
            ...datePayload,
          });
        }
        
        // Initialize branch onboarding placeholders (users/devices)
        const totalDevices = (createdBranch.numberOfNetworkDevices || 0) + (createdBranch.numberOfDevices || 0);
        await api.post(`/branches/${createdBranch.id}/onboarding/init`, {
          usersCount: createdBranch.numberOfUsers || 0,
          devicesCount: totalDevices,
        });
      }

      // Delete the draft after successful creation
      if (journeyData.draftId) {
        deleteDraft(journeyData.draftId);
      }

      toast.success('Company and branches created successfully!');
      
      // Call completion handler with created data
      onComplete({
        company: createdCompany,
        branches: createdBranches,
        totals,
      });
      
    } catch (error) {
      const details = error.response?.data;
      console.error('Error creating company:', {
        status: error.response?.status,
        data: details,
        payload: {
          company,
          branches,
          onboarding,
        },
      });
      let validationMsg = null;
      if (Array.isArray(details?.details) && details.details.length) {
        validationMsg = details.details
          .map(d => `${d.field || 'field'}: ${d.message}`)
          .join(' | ');
      }
      toast.error(validationMsg || details?.message || details?.error || 'Failed to create company. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            Review & Launch
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Review your configuration before creating the company and branches.
          </p>
        </div>

        <div className="space-y-4">
          {/* Company Summary */}
          <SummarySection
            title="Company Details"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
            onEdit={() => goToStep(1)}
          >
            <InfoRow label="Company Name" value={company.name} />
            <InfoRow label="Registration Number" value={company.registrationNumber} />
            <InfoRow label="VAT Number" value={company.vatNumber} />
            <InfoRow label="Address" value={[company.address, company.city, company.country].filter(Boolean).join(', ')} />
            <InfoRow label="Contact" value={`${company.contactPerson || ''} ${company.contactNumber ? `(${company.contactNumber})` : ''}`.trim()} />
            <InfoRow label="Email" value={company.emailAddress} />
            <InfoRow label="Payment Terms" value={company.paymentTerms} />
          </SummarySection>

          {/* Branches Summary */}
          <SummarySection
            title={`Branches (${branches.length})`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
            onEdit={() => goToStep(2)}
          >
            {branches.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">No branches added</p>
            ) : (
              <div className="space-y-3">
                {branches.map((branch, index) => (
                  <div key={branch.id || index} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {branch.name || `Branch ${index + 1}`}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {branch.city || 'No location'}
                      </span>
                    </div>
                    <div className="mt-2 flex gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span>{branch.numberOfUsers || 0} users</span>
                      <span>{branch.numberOfDevices || 0} devices</span>
                      <span>{branch.numberOfServers || 0} servers</span>
                      {branch.hasFirewall && <span className="text-green-600 dark:text-green-400">Firewall</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Totals */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-500">{totals.totalUsers}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">{totals.totalDevices}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Devices</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-500">{totals.totalServers}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Servers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-500">{totals.totalNetworkDevices}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Network</div>
                </div>
              </div>
            </div>
          </SummarySection>

          {/* Onboarding Summary */}
          <SummarySection
            title="Onboarding Configuration"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
            onEdit={() => goToStep(3)}
          >
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Internal Systems</h4>
                <div className="flex flex-wrap gap-2">
                  {onboarding.systems.length === 0 ? (
                    <span className="text-gray-500 text-sm">None selected</span>
                  ) : (
                    onboarding.systems.map(system => (
                      <span key={system} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-sm">
                        {system}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company Tasks</h4>
                <div className="flex flex-wrap gap-2">
                  {onboarding.companyTasks.length === 0 ? (
                    <span className="text-gray-500 text-sm">None selected</span>
                  ) : (
                    onboarding.companyTasks.map(task => (
                      <span key={task} className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-sm">
                        {task}
                      </span>
                    ))
                  )}
                </div>
              </div>

              {onboarding.hasServers && (
                <InfoRow label="Server Location" value={onboarding.serverLocation} />
              )}

              {(onboarding.onboardingStartDate || onboarding.onboardingEndDate) && (
                <div className="flex gap-6">
                  {onboarding.onboardingStartDate && (
                    <InfoRow label="Start Date" value={new Date(onboarding.onboardingStartDate).toLocaleDateString()} />
                  )}
                  {onboarding.onboardingEndDate && (
                    <InfoRow label="End Date" value={new Date(onboarding.onboardingEndDate).toLocaleDateString()} />
                  )}
                </div>
              )}
            </div>
          </SummarySection>

          {/* Team Assignment Summary */}
          <SummarySection
            title="Team Assignment"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
            onEdit={() => goToStep(3)}
          >
            {selectedTeamMembers.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">No team members assigned</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedTeamMembers.map(user => {
                  const getRoleBadgeColor = (role) => {
                    switch (role) {
                      case 'Admin':
                        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800';
                      case 'Manager':
                        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
                      case 'Technician':
                        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800';
                      default:
                        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
                    }
                  };
                  
                  return (
                    <span 
                      key={user.id} 
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border ${getRoleBadgeColor(user.role)}`}
                    >
                      <span className="font-medium">{user.name}</span>
                      <span className="text-xs opacity-75">({user.role})</span>
                    </span>
                  );
                })}
              </div>
            )}
          </SummarySection>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={onPrev}>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </Button>
          
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              variant="success"
              size="lg"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!company.name || !company.registrationNumber}
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Create Company & Start Onboarding
            </Button>
          </motion.div>
        </div>

        {(!company.name || !company.registrationNumber) && (
          <p className="mt-4 text-sm text-amber-600 dark:text-amber-400 text-center">
            Please fill in the required company details (name and registration number) before proceeding.
          </p>
        )}
      </Card>
    </div>
  );
}

